import { SvgConverterType } from "../../../reports/Report";
import {
  activityLevelLabel,
  genderLabel,
  goalLabel,
  heightLabel,
  weightLabel,
} from "../../../whiteLabel/customData";
import { formatReportDate } from "../../../whiteLabel/format";
import {
  metricDescription,
  metricName,
} from "../../../whiteLabel/metricKeys";
import { useRasterizedBlock } from "../../../whiteLabel/useRasterizedBlock";
import {
  SEVERITY_SCALE,
  severityForValue,
} from "../../../whiteLabel/whiteLabelTheme";

import KeyMetricsGlossary from "../components/KeyMetricsGlossary";
import PatientInfoTable, {
  PatientInfoField,
} from "../components/PatientInfoTable";
import SeverityScaleLegend from "../components/SeverityScaleLegend";
import WhiteLabelCard from "../components/WhiteLabelCard";
import WhiteLabelScoreBar from "../components/WhiteLabelScoreBar";
import WhiteLabelPage from "../WhiteLabelPage/WhiteLabelPage";
import { WhiteLabelPageProps } from "../types";

import classes from "./CoverResultsPage.module.css";

type Props = WhiteLabelPageProps & { svgConverter: SvgConverterType };

/**
 * The risk bar's two halves are CSS gradients, and jsPDF's html2canvas pass renders
 * them as solid black in the exported PDF. Pre-rasterizing the row and swapping in
 * the bitmap is the same workaround the default report applies to this exact element
 * (OverviewMetricBarGraphPage rasterizes its "riskBar" node).
 */
const RISK_SRC_ID = "wl-risk-src";
const RISK_OUT_ID = "wl-risk-out";


const CoverResultsPage = ({
  data,
  theme,
  lang,
  t,
  pageNumber,
  svgConverter,
}: Props): JSX.Element => {
  const cover = data?.PAGES?.find((page: any) => page.PAGE_TYPE === "COVER");
  const overview = data?.PAGES?.find(
    (page: any) => page.PAGE_TYPE === "OVERVIEW"
  );

  // Same flattening the default RMR flow uses for its per-metric pages.
  const metrics: any[] = overview?.ORGANS
    ? Object.values(overview.ORGANS).flatMap((organ: any) => [
        ...(organ.METRICS ?? []),
      ])
    : [];

  // Risk metrics sit outside the METRICS arrays (ORGANS.<organ>.RISK_METRICS) and
  // the default report renders them on the overview too. Their scale is a Low-to-High
  // continuum, so they use the range variant of the bar and follow the banded ones.
  const riskMetrics: any[] = overview?.ORGANS
    ? Object.values(overview.ORGANS).flatMap((organ: any) => [
        ...(organ.RISK_METRICS ?? []),
      ])
    : [];

  // Scale labels come from the dictionary, not OVERVIEW.NAVBAR: the band names are
  // the same on every report, and the backend serves them in English only. Ordered
  // by SEVERITY_SCALE so they cannot drift out of step with the bar colours.
  const tickLabels: string[] = SEVERITY_SCALE.map(
    (band) => t.severity.ticks[band.key]
  );

  /**
   * Score caption, e.g. "79% | Good".
   *
   * Composed here rather than taken from TITLE2, which the backend serves in
   * English. Deriving the band from SEVERITY_SCALE also means the word always
   * agrees with the bar colour, which previously matched only by coincidence.
   *
   * One metric (Metabolic Flexibility) reports a "High"/"Low" caption instead of a
   * percentage; there is no band to derive, so its TITLE2 is passed through and
   * stays English until the backend exposes that state as a code.
   */
  const scoreCaption = (metric: any): string | undefined => {
    const raw = String(metric.TITLE2 ?? "");
    if (!raw.includes("%")) return raw || undefined;
    const value = Number(metric.VALUE) || 0;
    const band = severityForValue(value);
    return `${value}% | ${t.severity.ticks[band.key]}`;
  };

  /**
   * Localizes the risk metric's Low/High state without inventing a threshold: the
   * payload supplies its own LOW and HIGH wording, so the caption's trailing word is
   * matched against those and swapped for the dictionary equivalent. Comparing
   * against the payload's own strings keeps this working if the backend localizes.
   */
  const riskCaption = (metric: any): string | undefined => {
    const caption = String(metric.TITLE2 ?? "");
    const high = String(metric.HIGH ?? "");
    const low = String(metric.LOW ?? "");
    if (high && caption.endsWith(high)) return t.severity.range.high;
    if (low && caption.endsWith(low)) return t.severity.range.low;
    return caption || undefined;
  };

  // Keyed on the language: the bar's title, description and Low/High caption all
  // come from the dictionary, so the bitmap is redrawn when the reader switches.
  const riskRasterized = useRasterizedBlock({
    svgConverter,
    srcId: RISK_SRC_ID,
    outId: RISK_OUT_ID,
    width: 706,
    enabled: riskMetrics.length > 0,
    redrawKey: lang,
  });

  // Weight / Height / Activity Level / Goal come from CUSTOM_DATA, the block of
  // raw questionnaire answers the backend added for this report. Any field the
  // payload omits stays a blank write-in rule, which is what every one of them was
  // before the block existed.
  const custom = data?.CUSTOM_DATA;

  // CUSTOM_DATA.gender is the questionnaire code, so it localizes; data.GENDER is
  // the same fact as an English-only string and is the fallback for payloads
  // served before the block was added.
  const gender = [
    genderLabel(custom, t.patientInfo) ?? data?.GENDER,
    data?.AGE ? `(${data.AGE})` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const fields: PatientInfoField[] = [
    { label: t.patientInfo.name, value: cover?.SUBJECT_NAME },
    { label: t.patientInfo.testType, value: t.patientInfo.testTypeValue },
    { label: t.patientInfo.gender, value: gender },
    { label: t.patientInfo.date, value: formatReportDate(cover?.DATE, lang) },
    {
      label: t.patientInfo.weight,
      value: weightLabel(custom, t.patientInfo, lang),
    },
    {
      label: t.patientInfo.activityLevel,
      value: activityLevelLabel(custom, t.patientInfo, lang),
    },
    {
      label: t.patientInfo.height,
      value: heightLabel(custom, t.patientInfo, lang),
    },
    { label: t.patientInfo.goal, value: goalLabel(custom, t.patientInfo) },
  ];

  return (
    <WhiteLabelPage
      theme={theme}
      lang={lang}
      brandLabelTemplate={t.footer.brandLabel}
      sectionName={t.sections.coverResults}
      pageNumber={pageNumber}
      dataAgentTarget="wl-cover-results"
    >
      <div className={classes.titleBlock}>
        <div className={classes.title}>{t.cover.title}</div>
        <div className={classes.subtitle}>{t.cover.subtitle}</div>
      </div>

      <div className={classes.infoCard}>
        <WhiteLabelCard padding="4px 16px">
          <PatientInfoTable fields={fields} />
        </WhiteLabelCard>
      </div>

      <div className={classes.sectionLabel}>{t.glossary.heading}</div>
      <KeyMetricsGlossary terms={t.glossary.terms} />

      <div className={classes.legendWrap}>
        <WhiteLabelCard padding="9px 12px">
          <SeverityScaleLegend severity={t.severity} />
        </WhiteLabelCard>
      </div>

      <div className={classes.sectionLabel}>{t.cover.resultsHeading}</div>

      <div className={classes.results}>
        <WhiteLabelCard>
          {/* Bars sit at their natural height with a fixed gap between them, so
              the rhythm stays the same whatever the metric count (7 in this test
              payload, 8 in the mock-up, more on Frank RMR). */}
          <div className={classes.resultsInner}>
            {metrics.map((metric) => (
              <WhiteLabelScoreBar
                key={`${metric.METRIC_TYPE}-${metric.TITLE1}`}
                title={metricName(metric.METRIC_TYPE, t.metrics, metric.TITLE1)}
                caption={scoreCaption(metric)}
                value={Number(metric.VALUE) || 0}
                tickLabels={tickLabels}
                description={metricDescription(metric.METRIC_TYPE, t.metrics)}
              />
            ))}

            <div id={RISK_OUT_ID}>
              {!riskRasterized && (
                <div id={RISK_SRC_ID}>
                  {riskMetrics.map((metric) => (
                    <WhiteLabelScoreBar
                      key={`risk-${metric.METRIC_TYPE}-${metric.TITLE1}`}
                      title={metricName(
                        metric.METRIC_TYPE,
                        t.metrics,
                        metric.TITLE1
                      )}
                      caption={riskCaption(metric)}
                      value={Number(metric.VALUE) || 0}
                      description={metricDescription(
                        metric.METRIC_TYPE,
                        t.metrics
                      )}
                      range={{
                        ...t.severity.range,
                        // VALUE === 100 is the high side; mirrors RiskChartBar in the
                        // default report so both surfaces agree on the same payload.
                        active: Number(metric.VALUE) === 100 ? "high" : "low",
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </WhiteLabelCard>
      </div>
    </WhiteLabelPage>
  );
};

export default CoverResultsPage;
